import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending", status: "open", kind: "product" });
  const [attempts, setAttempts] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const maxAttempts = 8;
    const poll = async (n) => {
      if (cancelled) return;
      if (n >= maxAttempts) {
        setTimedOut(true);
        return;
      }
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setStatus(res.data);
        setAttempts(n + 1);
        if (res.data.payment_status === "paid" || res.data.status === "expired") return;
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        if (!cancelled) setTimeout(() => poll(n + 1), 2500);
      }
    };
    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const paid = status.payment_status === "paid";

  return (
    <section
      data-testid="checkout-success-page"
      className="min-h-[70vh] bg-brand-cream paper-grain flex items-center justify-center px-6 py-24"
    >
      <div className="max-w-xl text-center">
        {paid ? (
          <CheckCircle2 className="w-16 h-16 text-brand-moss mx-auto mb-6" strokeWidth={1.4} />
        ) : timedOut ? (
          <Sparkles className="w-16 h-16 text-brand-gold mx-auto mb-6" />
        ) : (
          <Loader2 className="w-12 h-12 text-brand-moss/60 mx-auto mb-6 animate-spin" />
        )}

        <h1 className="font-serif text-4xl lg:text-5xl text-brand-moss leading-tight">
          {paid
            ? status.kind === "membership"
              ? "Welcome to the Circle."
              : "Thank you — your order is placed."
            : timedOut
            ? "We're still confirming…"
            : "Confirming your payment…"}
        </h1>
        <p className="mt-5 text-lg text-brand-moss/75 leading-relaxed">
          {paid
            ? "An email receipt is on its way. Each piece is hand-poured and arranged with intention — give us a few days to make yours."
            : timedOut
            ? "Your payment may still be processing. Please check your email for a Stripe receipt in a few minutes."
            : `Please don't close this page. Attempt ${attempts} of 8.`}
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/"
            data-testid="success-back-home"
            className="inline-flex items-center justify-center bg-brand-moss text-brand-gold hover:bg-brand-moss-light px-7 py-3 rounded-full text-sm tracking-wide transition-colors"
          >
            Back to the Sanctuary
          </Link>
          <Link
            to="/shop"
            className="inline-flex items-center justify-center border border-brand-moss/30 text-brand-moss px-7 py-3 rounded-full text-sm tracking-wide hover:bg-brand-moss/5 transition-colors"
          >
            Keep Exploring
          </Link>
        </div>

        {sessionId && (
          <p className="mt-8 text-xs text-brand-moss/40 break-all" data-testid="session-id">
            Order ref: {sessionId}
          </p>
        )}
      </div>
    </section>
  );
}
